import { format } from 'date-fns'
import { StorePerformanceItem } from '../types'
import { isValidDate } from './periodValidation'

export const buildQuestionnaireIds = (
  selectedQuestionnaires: StorePerformanceItem[],
  selectedVersionsByQuestionnaire?: Record<string, string[]>
): string[] => {
  const ids: string[] = []
  selectedQuestionnaires.forEach((q) => {
    if (q.versions && q.versions.length > 0) {
      const versionIds = selectedVersionsByQuestionnaire?.[q.id] || []
      ids.push(...versionIds)
    } else {
      ids.push(q.id)
    }
  })
  return ids
}

export const getConsultedQuestionnaireItems = (
  selectedQuestionnaires: StorePerformanceItem[],
  selectedVersionsByQuestionnaire: Record<string, string[]>
): StorePerformanceItem[] => {
  return selectedQuestionnaires.flatMap((q) => {
    if (q.versions && q.versions.length > 0) {
      const selectedVersionIds = selectedVersionsByQuestionnaire[q.id] || []
      return q.versions
        .filter((v) => selectedVersionIds.includes(String(v.id)))
        .map((v) => ({
          id: String(v.id),
          label: v.name,
          value: String(v.id),
        }))
    }
    return [q]
  })
}

interface BuildParamsInput {
  companyId: string
  selectedUnits: StorePerformanceItem[]
  selectedQuestionnaires: StorePerformanceItem[]
  appliedPeriodFrom: Date | null
  appliedPeriodTo: Date | null
  appliedPeriodYear: number
  appliedPeriodWeek: string[]
  filterDivision: StorePerformanceItem[]
  filterZone: StorePerformanceItem[]
  filterUser: StorePerformanceItem[]
  filterRole: StorePerformanceItem[]
  selectedVersionsByQuestionnaire?: Record<string, string[]>
}

export const buildQueryParams = (input: BuildParamsInput) => {
  const {
    companyId,
    selectedUnits,
    selectedQuestionnaires,
    appliedPeriodFrom,
    appliedPeriodTo,
    appliedPeriodYear,
    appliedPeriodWeek,
    filterDivision,
    filterZone,
    filterUser,
    filterRole,
    selectedVersionsByQuestionnaire,
  } = input

  const questionnaireIds = buildQuestionnaireIds(selectedQuestionnaires, selectedVersionsByQuestionnaire)

  return {
    company_id: companyId,
    store_ids: selectedUnits.map((u) => u.id).join(','),
    questionnaire_ids: questionnaireIds.join(','),
    date_init: isValidDate(appliedPeriodFrom) ? format(appliedPeriodFrom, 'yyyy-MM-dd') : undefined,
    date_end: isValidDate(appliedPeriodTo) ? format(appliedPeriodTo, 'yyyy-MM-dd') : undefined,
    year: appliedPeriodYear,
    weeks: appliedPeriodWeek.length > 0 ? appliedPeriodWeek.join(',') : undefined,
    division_ids: filterDivision.length > 0 ? filterDivision.map((d) => d.value).join(',') : undefined,
    zone_ids: filterZone.length > 0 ? filterZone.map((z) => z.value).join(',') : undefined,
    user_ids: filterUser.length > 0 ? filterUser.map((u) => u.value).join(',') : undefined,
    role_ids: filterRole.length > 0 ? filterRole.map((r) => r.value).join(',') : undefined,
  }
}

export const getCompanyId = (
  hasAssignedCompany: boolean,
  assignedCompany: { id: number | string } | null | undefined,
  selectedCompany: StorePerformanceItem | null
): string => {
  return hasAssignedCompany
    ? String(assignedCompany?.id)
    : String(selectedCompany?.value ?? '')
}
